import React from "react";
import { useTranslation } from "react-i18next";

// Question difficulty and the points it gives (the server's table: question.points follows difficulty).
export const DIFFICULTY_POINTS = { A: 3, B: 5, C: 10 };
export const DIFFICULTY_LEVELS = Object.keys(DIFFICULTY_POINTS);
export const isDifficulty = (value) => Object.prototype.hasOwnProperty.call(DIFFICULTY_POINTS, value);

// One hue family from easy to hard: green, amber and red already mean correct, warning and error here.
const TONES = {
  A: { badge: "bg-sky-50 text-sky-700 ring-sky-600/20", active: "bg-sky-600 text-white shadow-sm" },
  B: { badge: "bg-indigo-50 text-indigo-700 ring-indigo-600/20", active: "bg-indigo-600 text-white shadow-sm" },
  C: { badge: "bg-violet-50 text-violet-700 ring-violet-600/20", active: "bg-violet-600 text-white shadow-sm" },
};

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

// "B · 5 баллов"
export const difficultyLabel = (t, level) => `${level} · ${t("difficulty.points", { count: DIFFICULTY_POINTS[level] })}`;

// "B · 5" pill; nothing for a question without difficulty. `withLabel` shows "Сложность B · 5 баллов"
// ("Сложность B" with showPoints={false}, where the points are shown next to it); `count` shows "B × 3"
// (how many questions have it).
export function DifficultyBadge({ level, withLabel = false, showPoints = true, count, className = "" }) {
  const [t] = useTranslation();
  if (!isDifficulty(level)) return null;
  const title = t("difficulty.title", { label: difficultyLabel(t, level) });
  const counted = count !== undefined;
  return (
    <span
      title={counted ? `${title} × ${count}` : title}
      // relative: the sr-only text is positioned absolutely; without it, inside a scrolling table it escapes
      // the scroll box and widens the page.
      className={`relative inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${TONES[level].badge} ${className}`}
    >
      <span aria-hidden="true">
        {withLabel && <span className="font-normal">{t("difficulty.label")} </span>}
        {level}
        {counted ? (
          <span className="font-normal opacity-75"> × {count}</span>
        ) : (
          showPoints && (
            <span className="font-normal opacity-75">
              {" · "}
              {withLabel ? t("difficulty.points", { count: DIFFICULTY_POINTS[level] }) : DIFFICULTY_POINTS[level]}
            </span>
          )
        )}
      </span>
      <span className="sr-only">{counted ? `${title} × ${count}` : title}</span>
    </span>
  );
}

/**
 * A / B / C toggle. `value` is a level or null; onChange(level | null).
 * With `noneLabel` a first button picks "not set"; without it, clicking the picked level clears it.
 * `compact` shows "A · 3" (the full points go to the tooltip), otherwise "A · 3 балла".
 */
export function DifficultyPicker({ value, onChange, disabled = false, noneLabel, compact = false, label, className = "" }) {
  const [t] = useTranslation();
  const current = isDifficulty(value) ? value : null;
  const size = compact ? "h-7 px-2 text-xs" : "h-9 px-3 text-sm";
  const button = (active, activeClass) =>
    `inline-flex items-center gap-1 whitespace-nowrap rounded-md font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${size} ${focusRing} ${
      active ? activeClass : "text-slate-600 enabled:hover:bg-slate-100 enabled:hover:text-slate-900"
    }`;

  return (
    <div
      role="group"
      aria-label={label || t("difficulty.label")}
      className={`inline-flex flex-wrap items-center gap-0.5 rounded-lg bg-white p-0.5 ring-1 ring-inset ring-slate-300 ${className}`}
    >
      {noneLabel && (
        <button
          type="button"
          aria-pressed={!current}
          disabled={disabled}
          onClick={() => onChange(null)}
          className={button(!current, "bg-slate-700 text-white shadow-sm")}
        >
          {noneLabel}
        </button>
      )}
      {DIFFICULTY_LEVELS.map((level) => {
        const active = current === level;
        const full = difficultyLabel(t, level);
        return (
          <button
            key={level}
            type="button"
            aria-pressed={active}
            aria-label={t("difficulty.title", { label: full })}
            title={full}
            disabled={disabled}
            onClick={() => onChange(active && !noneLabel ? null : level)}
            className={button(active, TONES[level].active)}
          >
            <span className="font-semibold">{level}</span>
            <span className={active ? "opacity-80" : "text-slate-400"}>
              · {compact ? DIFFICULTY_POINTS[level] : t("difficulty.points", { count: DIFFICULTY_POINTS[level] })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
